import styled from '@emotion/styled';
import { Constants, Theme } from '@seedlang/constants';
import { AppUI } from '@seedlang/state';
import { isPresent } from '@seedlang/utils';
import { observer } from 'mobx-react';
import React from 'react';
import Link from 'components/link';
import Alert from 'components/alert';
import Button from 'components/button/button';
import GoProButton from 'components/button/go_pro_button';
import InPlaceSelect from 'components/form/in_place_select';
import FormLabel from 'components/form_factory/form_label';
import ReviewImportPreviewTable, {
  REVIEW_IMPORT_MAX_ROWS,
  getReviewImportPreviewRows,
  reviewImportRowHasOverflow,
} from 'components/review/review_import_preview_table';

const Wrapper = styled.div`
`;

const FormGroup = styled.div`
  margin: ${props => props.margin || '0 0 20px 0'};
`;

const Section = styled.div`
  margin: 0 0 20px 0;
`;

const SectionTitle = styled.div`
  color: #333;
  font-size: 18px;
  font-weight: bold;
  margin: 0 0 18px 0;
`;

const HelpBox = styled.div`
  background: #F3F4F6;
  border-radius: 10px;
  padding: 16px;
  margin: 0 0 20px 0;
`;

const HelpBoxText = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: #555;
`;

const FieldHelpBox = styled.div`
  font-size: 13px;
  line-height: 18px;
  margin: 8px 0 0 0;
  color: #666;
`;

const HelpActions = styled.div`
  margin-top: 12px;
  text-align: center;
`;

const GoProLink = styled(Link)`
  display: inline-block;
`;

const FieldLabel = styled(FormLabel)`
  color: #333;
  display: block;
  margin-bottom: 6px;
  font-size: 14px !important;
  text-transform: none !important;
`;

const FileInput = styled.input`
  display: block;
  width: 100%;
  padding: 10px;
  font-size: 14px;
  border: 1px solid #CCC;
  border-radius: 3px;
  box-sizing: border-box;
  background: #FFF;
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
`;

const HelpCenterText = styled.div`
  display: flex;
  align-items: center;
  color: #666;
  font-size: 13px;
  line-height: 18px;
  margin: 10px 0 0 10px;

  a {
    color: ${Theme.blue};
    font-weight: bold;
  }
`;

@observer
class ReviewImport extends React.Component {
  reviewCreatedMessageRef = React.createRef();

  state = {
    previewFileText: null,
    previewErrorMessage: null,
  };

  cardTypeOptions = [
    ['translation', 'Translation'],
    ['comprehension', 'Comprehension'],
  ];

  separatorOptions = [
    ['comma', 'Comma'],
    ['semicolon', 'Semicolon'],
    ['tab', 'Tab'],
  ];

  headerRowOptions = [
    ['true', 'Yes'],
    ['false', 'No'],
  ];

  componentDidUpdate(prevProps) {
    if (prevProps.importFile && !this.props.importFile && this.state.previewFileText !== null) {
      this.clearPreview();
    }
    if (prevProps.reviewCreatedMessage !== this.props.reviewCreatedMessage && this.props.reviewCreatedMessage) {
      requestAnimationFrame(() => {
        if (this.reviewCreatedMessageRef.current) {
          this.reviewCreatedMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    }
  }

  onSetImportFile = (event) => {
    this.clearPreview();
    this.props.onSetImportFile(event);
  }

  onSetImportCardType = (importCardType) => {
    this.clearPreview();
    this.props.onSetImportCardType(importCardType);
  }

  onSetImportSeparator = (importSeparator) => {
    this.clearPreview();
    this.props.onSetImportSeparator(importSeparator);
  }

  onSetImportHasHeaderRow = (importHasHeaderRow) => {
    this.clearPreview();
    this.props.onSetImportHasHeaderRow(importHasHeaderRow);
  }

  onPreview = () => {
    if (!this.props.importFile) {
      return;
    }
    const reader = new FileReader();
    reader.onload = event => {
      this.setState({
        previewFileText: event.target.result,
        previewErrorMessage: null,
      });
    };
    reader.onerror = () => {
      this.setState({
        previewFileText: null,
        previewErrorMessage: 'The file could not be previewed.',
      });
    };
    reader.readAsText(this.props.importFile);
  }

  clearPreview = () => {
    this.setState({
      previewFileText: null,
      previewErrorMessage: null,
    });
  }

  onCancel = () => {
    this.clearPreview();
    this.props.onCancel();
  }

  render() {
    const previewRows = this.state.previewFileText !== null ? getReviewImportPreviewRows({
      fileText: this.state.previewFileText,
      separator: this.props.importSeparator,
      hasHeaderRow: this.props.importHasHeaderRow,
    }) : [];
    const previewOverflowRows = previewRows.filter(row => reviewImportRowHasOverflow(row));
    return (
      <Wrapper>
        {
          !this.props.canImportReviews &&
          <HelpBox>
            <HelpBoxText>
              Review imports are available to Pro members on the web app. Upgrade to Pro if you would like to upload a CSV or TXT file and create reviews in bulk.
              <HelpActions>
                <GoProLink to='memberships.index'>
                  <GoProButton
                    height='42px'
                    marginLeft='0'
                  >
                    <div className='text'>
                      <span>Go Pro</span>
                    </div>
                  </GoProButton>
                </GoProLink>
              </HelpActions>
            </HelpBoxText>
          </HelpBox>
        }
        {
          this.props.canImportReviews &&
          <>
            <Section>
              <SectionTitle>
                1. Set Up Import
              </SectionTitle>
              <FormGroup>
                <FieldLabel
                  name='review_import_deck'
                  label='Target Deck'
                />
                <InPlaceSelect
                  allowUpdate
                  options={this.props.reviewDeckOptions}
                  value={this.props.deckId}
                  onChange={val => this.props.onSetDeckId(val)}
                />
                <FieldHelpBox>
                  Choose the review deck where the imported cards should be added.
                </FieldHelpBox>
              </FormGroup>
              <FormGroup>
                <FieldLabel
                  name='review_import_card_type'
                  label='Card Type'
                />
                <InPlaceSelect
                  allowUpdate
                  options={this.cardTypeOptions}
                  value={this.props.importCardType}
                  onChange={this.onSetImportCardType}
                />
                <FieldHelpBox>
                  {`Choose Translation to review from the translation back to the ${isPresent(AppUI.targetLanguageName) ? AppUI.targetLanguageName + ' ' : ''}word. Choose Comprehension to review your understanding of the ${AppUI.targetLanguageName} word.`}
                </FieldHelpBox>
                <FieldHelpBox>
                  All words that are found in Seedlang's dictionary will be added as a Comprehension or Translation card, with a video. Others will be added as Custom cards.
                </FieldHelpBox>
              </FormGroup>
            </Section>
            <Section>
              <SectionTitle>
                2. Upload File
              </SectionTitle>
              <FormGroup>
                <FieldLabel
                  name='review_import_file'
                  label='CSV, or TXT File'
                  htmlFor='review-import-file'
                />
                <FileInput
                  id='review-import-file'
                  key={this.props.importFileInputKey}
                  type='file'
                  accept='.csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain'
                  onChange={this.onSetImportFile}
                />
                <FieldHelpBox>
                  {`Upload a .csv or .txt file with up to 100 rows. Use three columns in this order: ${isPresent(AppUI.targetLanguageName) ? AppUI.targetLanguageName : ''} word, translation, note.`}
                </FieldHelpBox>
                <FieldHelpBox>
                  The first two columns are limited to {Constants.REVIEW_CARD_MAX_FRONT_TEXT_LENGTH} characters each. Notes are limited to {Constants.REVIEW_CARD_MAX_NOTE_LENGTH} characters. For dictionary matches, the translation and note are saved together as the review note.
                </FieldHelpBox>
              </FormGroup>
              {
                this.props.importFile &&
                <>
                  <FormGroup>
                    <FieldLabel
                      name='review_import_separator'
                      label='Separator'
                    />
                    <InPlaceSelect
                      allowUpdate
                      options={this.separatorOptions}
                      value={this.props.importSeparator}
                      onChange={this.onSetImportSeparator}
                    />
                    <FieldHelpBox>
                      Choose the character that separates columns in your file.
                    </FieldHelpBox>
                  </FormGroup>
                  <FormGroup>
                    <FieldLabel
                      name='review_import_header_row'
                      label='Ignore First Row'
                    />
                    <InPlaceSelect
                      allowUpdate
                      options={this.headerRowOptions}
                      value={`${this.props.importHasHeaderRow}`}
                      onChange={val => this.onSetImportHasHeaderRow(val === 'true')}
                    />
                    <FieldHelpBox>
                      Select "Yes" if the first row contains column names like "word", "translation", and "note". That row will not be imported.
                    </FieldHelpBox>
                  </FormGroup>
                </>
              }
            </Section>
            {
              this.state.previewFileText !== null &&
              <Section>
                <SectionTitle>
                  3. Review File
                </SectionTitle>
                <FormGroup>
                  <FieldHelpBox>
                    Check that the columns and rows look correct before importing. If the formatting is wrong, modify your file or choose a different separator and preview the file again.
                  </FieldHelpBox>
                  <ReviewImportPreviewTable
                    fileText={this.state.previewFileText}
                    separator={this.props.importSeparator}
                    hasHeaderRow={this.props.importHasHeaderRow}
                  />
                  {
                    previewRows.some(row => `${row[2]}`.trim().length === 0) &&
                    <Alert
                      margin='15px 0 0 0'
                    >
                      Some rows have an empty translation. If the word isn't found in our dictionary, we won't be able to create a custom card without a translation.
                    </Alert>
                  }
                  {
                    previewRows.length > REVIEW_IMPORT_MAX_ROWS &&
                    <Alert
                      state='failure'
                      margin='15px 0 0 0'
                    >
                      Review imports are limited to {REVIEW_IMPORT_MAX_ROWS} rows. This file has {previewRows.length} rows. Remove {previewRows.length - REVIEW_IMPORT_MAX_ROWS} {previewRows.length - REVIEW_IMPORT_MAX_ROWS === 1 ? 'row' : 'rows'} and preview the file again.
                    </Alert>
                  }
                  {
                    previewOverflowRows.length > 0 &&
                    <Alert
                      margin='15px 0 0 0'
                    >
                      Some rows contain text that is longer than Seedlang can store. Long values will be truncated during import, including dictionary rows where the translation and note together exceed {Constants.REVIEW_CARD_MAX_NOTE_LENGTH} characters. Affected rows: {previewOverflowRows.slice(0, 10).map(row => row[0]).join(', ')}{previewOverflowRows.length > 10 ? `, and ${previewOverflowRows.length - 10} more` : ''}.
                    </Alert>
                  }
                </FormGroup>
              </Section>
            }
            <ButtonRow>
              {
                this.state.previewFileText === null &&
                <Button
                  onClick={this.onPreview}
                  backgroundOnActive={Theme.blueGray}
                  disabled={this.props.disableSubmit || this.props.showSpinner}
                  margin='0'
                >
                  Review File
                </Button>
              }
              {
                this.state.previewFileText !== null &&
                <Button
                  onClick={() => this.props.onSubmit(null)}
                  backgroundOnActive={Theme.blueGray}
                  disabled={this.props.disableSubmit || this.props.showSpinner || previewRows.length > REVIEW_IMPORT_MAX_ROWS}
                  margin='0'
                >
                  <i
                    className='fa fa-upload'
                    style={{ marginRight: 5, color: 'white' }}
                  />
                  Import Reviews
                </Button>
              }
              <Button
                onClick={this.onCancel}
                disabled={this.props.disableCancel || this.props.showSpinner}
                background={Theme.red}
                margin='0'
              >
                <i
                  className='fa fa-times'
                  style={{ marginRight: 5, color: 'white' }}
                />
                Cancel
              </Button>
            </ButtonRow>
            <HelpCenterText>
              <i className='fa fa-question-circle' style={{ marginRight: 5 }} />
              Need help formatting your file? Head to the&nbsp;<a href='https://seedlang.com/help_center/topic/7d2bdc2a-34ec-414a-971c-afb00c9291a5/article/9b7551eb-4d16-41bb-8080-cfee48425735' target='_blank' rel='noopener noreferrer'>Help Center</a>.
            </HelpCenterText>
            {
              this.state.previewErrorMessage &&
              <Alert
                state='failure'
                margin='20px 0 0 0'
                showCloseButton
                onClose={this.clearPreview}
              >
                {this.state.previewErrorMessage}
              </Alert>
            }
            {
              this.props.reviewCreatedMessage &&
              <div ref={this.reviewCreatedMessageRef}>
                <Alert
                  state='success'
                  margin='20px 0 0 0'
                >
                  {this.props.reviewCreatedMessage}
                </Alert>
              </div>
            }
          </>
        }
      </Wrapper>
    );
  }
}

export default ReviewImport;
